import React from 'react';
import { format, isAfter, isBefore, isSameDay } from 'date-fns';
import { Wallet, ArrowDown, Receipt, AlertCircle, ArrowRight, Trash2, ArrowLeft, GripVertical, X, Eye, EyeOff } from 'lucide-react';
import { collection, query, where, onSnapshot, doc, updateDoc, Timestamp, writeBatch } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';
import useEvents from '../hooks/useEvents';
import { BudgetBucket, BucketTransaction } from '../types';

interface TransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  sourceBucket: BudgetBucket;
  buckets: BudgetBucket[];
  onTransfer: (targetBucketId: string, amount: number) => Promise<void>;
}

function TransferModal({ isOpen, onClose, sourceBucket, buckets, onTransfer }: TransferModalProps) {
  const [targetBucketId, setTargetBucketId] = React.useState('');
  const [amount, setAmount] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const transferAmount = parseFloat(amount);
    if (transferAmount <= 0) {
      setError('Transfer amount must be greater than 0');
      return;
    }

    if (transferAmount > sourceBucket.remainingAmount) {
      setError('Transfer amount exceeds available balance');
      return;
    }

    try {
      await onTransfer(targetBucketId, transferAmount);
      onClose();
    } catch (error) {
      setError('Failed to transfer funds');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Transfer Funds</h3>
        
        {error && (
          <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-400 text-red-700">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
              {error}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">From</label>
            <div className="mt-1 p-2 bg-gray-50 rounded-md">
              {sourceBucket.name} (${sourceBucket.remainingAmount.toFixed(2)} available)
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">To</label>
            <select
              value={targetBucketId}
              onChange={(e) => setTargetBucketId(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              required
            >
              <option value="">Select a bucket</option>
              {buckets
                .filter(b => b.id !== sourceBucket.id)
                .map(bucket => (
                  <option key={bucket.id} value={bucket.id}>
                    {bucket.name} (${bucket.remainingAmount.toFixed(2)} available)
                  </option>
                ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Amount</label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <span className="text-gray-500 sm:text-sm">$</span>
              </div>
              <input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="block w-full pl-7 rounded-md border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
            >
              Transfer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function MobileBuckets() {
  const [selectedBucket, setSelectedBucket] = React.useState<BudgetBucket | null>(null);
  const [showTransactionForm, setShowTransactionForm] = React.useState(false);
  const [showTransferModal, setShowTransferModal] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [isReordering, setIsReordering] = React.useState(false);
  const [bucketOrder, setBucketOrder] = React.useState<string[]>([]);
  const [showEmptyBuckets, setShowEmptyBuckets] = React.useState(true); // Changed to true by default
  const { user } = useAuth();
  const { events } = useEvents();
  const [buckets, setBuckets] = React.useState<BudgetBucket[]>([]);

  const [transactionForm, setTransactionForm] = React.useState({
    amount: '',
    description: ''
  });

  React.useEffect(() => {
    if (!user) return;

    const bucketsQuery = query(
      collection(db, 'budgetBuckets'),
      where('userId', '==', user.uid)
    );

    const unsubscribe = onSnapshot(bucketsQuery, (snapshot) => {
      const bucketsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as BudgetBucket[];
      setBuckets(bucketsList);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching buckets:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  const filteredBuckets = React.useMemo(() => {
    return showEmptyBuckets 
      ? buckets 
      : buckets.filter(bucket => Math.abs(bucket.remainingAmount) > 0.001);
  }, [buckets, showEmptyBuckets]);

  const handleAddTransaction = async (e: React.FormEvent, bucketId: string) => {
    e.preventDefault();
    if (!user || !selectedBucket) return;

    try {
      const amount = parseFloat(transactionForm.amount);
      
      if (amount > selectedBucket.remainingAmount) {
        setError('Transaction amount exceeds remaining budget');
        return;
      }

      const transaction = {
        id: crypto.randomUUID(),
        amount: -amount,
        description: transactionForm.description,
        date: Timestamp.now()
      };

      const bucketRef = doc(db, 'budgetBuckets', bucketId);
      await updateDoc(bucketRef, {
        remainingAmount: selectedBucket.remainingAmount - amount,
        transactions: [...(selectedBucket.transactions || []), transaction]
      });

      setTransactionForm({ amount: '', description: '' });
      setShowTransactionForm(false);
      setError(null);
    } catch (error) {
      console.error('Error adding transaction:', error);
      setError('Failed to add transaction');
    }
  };

  const handleDeleteTransaction = async (bucketId: string, transaction: BucketTransaction) => {
    if (!user) return;

    try {
      const bucket = buckets.find(b => b.id === bucketId);
      if (!bucket) return;

      const isTransferFrom = transaction.description.startsWith('Transfer to ');
      const isTransferTo = transaction.description.startsWith('Transfer from ');

      if (isTransferFrom || isTransferTo) {
        let otherBucketName, otherTransactionDescription;
        if (isTransferFrom) {
          otherBucketName = transaction.description.replace('Transfer to ', '');
          otherTransactionDescription = `Transfer from ${bucket.name}`;
        } else {
          otherBucketName = transaction.description.replace('Transfer from ', '');
          otherTransactionDescription = `Transfer to ${bucket.name}`;
        }

        const otherBucket = buckets.find(b => b.name === otherBucketName);
        if (otherBucket) {
          const otherTransaction = otherBucket.transactions.find(t => 
            t.description === otherTransactionDescription &&
            Math.abs(t.amount) === Math.abs(transaction.amount)
          );

          if (otherTransaction) {
            const batch = writeBatch(db);

            const newRemainingAmount = bucket.remainingAmount - transaction.amount;
            const updatedTransactions = bucket.transactions.filter(t => t.id !== transaction.id);
            batch.update(doc(db, 'budgetBuckets', bucketId), {
              remainingAmount: newRemainingAmount,
              transactions: updatedTransactions
            });

            const otherNewRemainingAmount = otherBucket.remainingAmount - otherTransaction.amount;
            const otherUpdatedTransactions = otherBucket.transactions.filter(t => t.id !== otherTransaction.id);
            batch.update(doc(db, 'budgetBuckets', otherBucket.id), {
              remainingAmount: otherNewRemainingAmount,
              transactions: otherUpdatedTransactions
            });

            await batch.commit();
            return;
          }
        }
      }

      const newRemainingAmount = bucket.remainingAmount - transaction.amount;
      const updatedTransactions = bucket.transactions.filter(t => t.id !== transaction.id);

      const bucketRef = doc(db, 'budgetBuckets', bucketId);
      await updateDoc(bucketRef, {
        remainingAmount: newRemainingAmount,
        transactions: updatedTransactions
      });
    } catch (error) {
      console.error('Error deleting transaction:', error);
      setError('Failed to delete transaction');
    }
  };

  const handleTransfer = async (targetBucketId: string, amount: number) => {
    if (!selectedBucket) return;

    try {
      const targetBucket = buckets.find(b => b.id === targetBucketId);
      if (!targetBucket) throw new Error('Invalid target bucket');

      const batch = writeBatch(db);

      const sourceRef = doc(db, 'budgetBuckets', selectedBucket.id);
      batch.update(sourceRef, {
        remainingAmount: selectedBucket.remainingAmount - amount,
        transactions: [...selectedBucket.transactions, {
          id: crypto.randomUUID(),
          amount: -amount,
          description: `Transfer to ${targetBucket.name}`,
          date: Timestamp.now()
        }]
      });

      const targetRef = doc(db, 'budgetBuckets', targetBucketId);
      batch.update(targetRef, {
        remainingAmount: targetBucket.remainingAmount + amount,
        transactions: [...targetBucket.transactions, {
          id: crypto.randomUUID(),
          amount: amount,
          description: `Transfer from ${selectedBucket.name}`,
          date: Timestamp.now()
        }]
      });

      await batch.commit();
      setShowTransferModal(null);
    } catch (error) {
      console.error('Error transferring funds:', error);
      throw error;
    }
  };

  const handleReorder = (draggedId: string, targetId: string) => {
    if (!user) return;

    const newOrder = [...bucketOrder];
    const draggedIndex = newOrder.indexOf(draggedId);
    const targetIndex = newOrder.indexOf(targetId);

    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedId);

    setBucketOrder(newOrder);
    localStorage.setItem(`bucketOrder_${user.uid}`, JSON.stringify(newOrder));

    const sortedBuckets = [...buckets].sort((a, b) => {
      const indexA = newOrder.indexOf(a.id);
      const indexB = newOrder.indexOf(b.id);
      return indexA - indexB;
    });

    setBuckets(sortedBuckets);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (selectedBucket) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="bg-indigo-600 text-white p-4">
          <h1 className="text-xl font-bold">{selectedBucket.name}</h1>
          <div className="text-2xl font-bold mt-2">
            ${selectedBucket.remainingAmount.toFixed(2)}
          </div>
          <div className="text-sm opacity-75">available</div>
        </div>

        <div className="p-4">
          {error && (
            <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          <button
            onClick={() => setSelectedBucket(null)}
            className="w-full mb-4 flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Buckets
          </button>

          {showTransactionForm ? (
            <form onSubmit={(e) => handleAddTransaction(e, selectedBucket.id)} className="bg-white rounded-lg shadow-lg p-4 mb-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Amount</label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <span className="text-gray-500 sm:text-sm">$</span>
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      value={transactionForm.amount}
                      onChange={(e) => setTransactionForm({ ...transactionForm, amount: e.target.value })}
                      className="block w-full pl-7 rounded-md border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <input
                    type="text"
                    value={transactionForm.description}
                    onChange={(e) => setTransactionForm({ ...transactionForm, description: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowTransactionForm(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
                  >
                    Record Spending
                  </button>
                </div>
              </div>
            </form>
          ) : (
            <div className="grid grid-cols-2 gap-4 mb-4">
              <button
                onClick={() => setShowTransactionForm(true)}
                className="w-full bg-red-600 text-white rounded-lg py-3 px-4 flex items-center justify-center"
              >
                <ArrowDown className="w-5 h-5 mr-2" />
                Spend
              </button>
              <button
                onClick={() => setShowTransferModal(selectedBucket.id)}
                className="w-full bg-indigo-600 text-white rounded-lg py-3 px-4 flex items-center justify-center"
              >
                <ArrowRight className="w-5 h-5 mr-2" />
                Transfer
              </button>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-lg p-4">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Recent Transactions</h2>
            <div className="space-y-4">
              {selectedBucket.transactions
                .sort((a, b) => {
                  // Convert Firestore Timestamp to Date for comparison
                  const dateA = a.date instanceof Timestamp ? a.date.toDate() : new Date(a.date);
                  const dateB = b.date instanceof Timestamp ? b.date.toDate() : new Date(b.date);
                  return dateB.getTime() - dateA.getTime();
                })
                .map((transaction) => {
                  // Convert Timestamp to Date for formatting
                  const transactionDate = transaction.date instanceof Timestamp 
                    ? transaction.date.toDate() 
                    : new Date(transaction.date);

                  return (
                    <div
                      key={transaction.id}
                      className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0"
                    >
                      <div>
                        <div className="font-medium text-gray-900">{transaction.description}</div>
                        <div className="text-sm text-gray-500">
                          {format(transactionDate, 'MMM d, yyyy')}
                        </div>
                      </div>
                      <div className="flex items-center">
                        <span className={`font-medium ${
                          transaction.description === 'Added Funds' || 
                          transaction.description.startsWith('Transfer from') ||
                          Math.abs(transaction.amount) === transaction.amount
                            ? 'text-green-600'
                            : 'text-red-600'
                        }`}>
                          {transaction.description === 'Added Funds' || transaction.description.startsWith('Transfer from')
                            ? '+'
                            : '-'}${Math.abs(transaction.amount).toFixed(2)}
                        </span>
                        <button
                          onClick={() => handleDeleteTransaction(selectedBucket.id, transaction)}
                          className="ml-3 p-1 text-gray-400 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}

              {selectedBucket.transactions.length === 0 && (
                <div className="text-center py-6 text-gray-500">
                  No transactions yet
                </div>
              )}
            </div>
          </div>
        </div>

        {showTransferModal && (
          <TransferModal
            isOpen={showTransferModal}
            onClose={() => setShowTransferModal(null)}
            sourceBucket={selectedBucket}
            buckets={buckets}
            onTransfer={handleTransfer}
          />
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-indigo-600 text-white p-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <Wallet className="w-6 h-6 mr-2" />
            <h1 className="text-xl font-bold">My Buckets</h1>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setShowEmptyBuckets(!showEmptyBuckets)}
              className="text-white px-3 py-1 rounded-md bg-indigo-500 hover:bg-indigo-400 flex items-center"
            >
              {showEmptyBuckets ? (
                <>
                  <EyeOff className="w-4 h-4 mr-1" />
                  Hide Empty
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 mr-1" />
                  Show All
                </>
              )}
            </button>
            <button
              onClick={() => setIsReordering(!isReordering)}
              className="text-white px-3 py-1 rounded-md bg-indigo-500 hover:bg-indigo-400"
            >
              {isReordering ? 'Done' : 'Reorder'}
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-2">
        {filteredBuckets
          .map((bucket) => (
            <div
              key={bucket.id}
              className="bg-white rounded-lg shadow p-3 text-left relative"
              draggable={isReordering}
              onDragStart={(e) => e.dataTransfer.setData('text/plain', bucket.id)}
              onDragOver={(e) => {
                e.preventDefault();
                e.currentTarget.classList.add('bg-gray-50');
              }}
              onDragLeave={(e) => {
                e.currentTarget.classList.remove('bg-gray-50');
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove('bg-gray-50');
                const draggedId = e.dataTransfer.getData('text/plain');
                handleReorder(draggedId, bucket.id);
              }}
            >
              <div 
                className="flex items-center justify-between"
                onClick={() => !isReordering && setSelectedBucket(bucket)}
              >
                <div className="flex items-center flex-1 min-w-0">
                  {isReordering && (
                    <GripVertical className="w-5 h-5 text-gray-400 mr-2 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 truncate">{bucket.name}</h3>
                  </div>
                  <div className="ml-4 text-right">
                    <div className="text-lg font-bold text-indigo-600">
                      ${bucket.remainingAmount.toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

        {filteredBuckets.length === 0 && (
          <div className="text-center py-12">
            <Wallet className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {showEmptyBuckets ? 'No budget buckets' : 'No buckets with available balance'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {showEmptyBuckets 
                ? 'Create budget buckets in the desktop version'
                : (
                  <>
                    No buckets with available balance found.{' '}
                    <button
                      onClick={() => setShowEmptyBuckets(true)}
                      className="text-indigo-600 hover:text-indigo-800"
                    >
                      Show all buckets
                    </button>
                  </>
                )
              }
            </p>
          </div>
        )}
      </div>

      {showTransferModal && selectedBucket && (
        <TransferModal
          isOpen={true}
          onClose={() => setShowTransferModal(null)}
          sourceBucket={selectedBucket}
          buckets={buckets}
          onTransfer={handleTransfer}
        />
      )}
    </div>
  );
}